import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FiInbox,
    FiArrowLeft,
    FiCheck,
    FiCheckCircle,
    FiRotateCcw,
} from "react-icons/fi";
import {
    PageHeader,
    StatusBadge,
    EmptyState,
    useToast,
} from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { contactApi } from "../../api/endpoints/contact";
import { formatDateTimeDisplay } from "../../utils/dates";
import "./AdminHelpMessageDetail.css";

const STATUS_SEVERITY = {
    new: "info",
    read: "warning",
    resolved: "success",
};

const formatDate = (iso) => formatDateTimeDisplay(iso, { withSeconds: true });

const AdminHelpMessageDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const m = await contactApi.get(id);
                if (cancelled) return;
                setMsg(m);
            } catch (e) {
                if (cancelled) return;
                const code = e?.response?.status;
                setError(
                    code === 404
                        ? "Message not found"
                        : e?.response?.data?.message || e?.message || "Failed to load message"
                );
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const changeStatus = async (next) => {
        if (!msg || msg.status === next || busy) return;
        setBusy(true);
        try {
            const updated = await contactApi.updateStatus(msg.id, next);
            setMsg(updated);
            toast.success(`Marked as ${next}.`);
        } catch (e) {
            toast.error(
                e?.response?.data?.message || e?.message || "Failed to update status"
            );
        } finally {
            setBusy(false);
        }
    };

    if (error) {
        return (
            <div className="admin-detail-wrapper">
                <PageHeader
                    title="Help Message"
                    subtitle="View and update a user-submitted message"
                    icon={FiInbox}
                    actions={
                        <PageButton
                            type="button"
                            variant="ghost"
                            startIcon={<FiArrowLeft size={16} />}
                            onClick={() => navigate("/admin/messages")}
                        >
                            Back
                        </PageButton>
                    }
                />
                <div className="admin-detail-body">
                    <EmptyState
                        illustration={error === "Message not found" ? "search" : "error"}
                        title={error === "Message not found" ? "Message not found" : "Couldn't load this message"}
                        description={
                            error === "Message not found"
                                ? "It may have been deleted, or the link is wrong."
                                : error
                        }
                        action={
                            <PageButton
                                type="button"
                                variant="primary"
                                onClick={() => navigate("/admin/messages")}
                            >
                                Back to messages
                            </PageButton>
                        }
                    />
                </div>
            </div>
        );
    }

    if (!msg) {
        return (
            <div className="admin-detail-wrapper">
                <PageHeader
                    title="Help Message"
                    subtitle="Loading…"
                    icon={FiInbox}
                />
                <div className="admin-detail-body">
                    <div className="admin-detail-skeleton" />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-detail-wrapper">
            <PageHeader
                title={`Message #${msg.id}`}
                subtitle={`From ${msg.userEmail}`}
                icon={FiInbox}
                actions={
                    <PageButton
                        type="button"
                        variant="ghost"
                        startIcon={<FiArrowLeft size={16} />}
                        onClick={() => navigate("/admin/messages")}
                    >
                        Back
                    </PageButton>
                }
            />

            <div className="admin-detail-body">
                <div className="admin-detail-toolbar">
                    <div className="admin-detail-status">
                        <span className="admin-detail-status-label">Status</span>
                        <StatusBadge
                            severity={STATUS_SEVERITY[msg.status] || "neutral"}
                        >
                            {msg.status}
                        </StatusBadge>
                    </div>
                    <div className="admin-detail-actions">
                        {msg.status !== "read" && (
                            <PageButton
                                type="button"
                                variant="secondary"
                                startIcon={<FiCheck size={16} />}
                                onClick={() => changeStatus("read")}
                                disabled={busy}
                            >
                                Mark as Read
                            </PageButton>
                        )}
                        {msg.status !== "resolved" && (
                            <PageButton
                                type="button"
                                variant="success"
                                startIcon={<FiCheckCircle size={16} />}
                                onClick={() => changeStatus("resolved")}
                                disabled={busy}
                            >
                                Mark as Resolved
                            </PageButton>
                        )}
                        {msg.status !== "new" && (
                            <PageButton
                                type="button"
                                variant="ghost"
                                startIcon={<FiRotateCcw size={16} />}
                                onClick={() => changeStatus("new")}
                                disabled={busy}
                            >
                                Reopen
                            </PageButton>
                        )}
                    </div>
                </div>

                <div className="admin-detail-card">
                    <dl className="admin-detail-grid">
                        <div>
                            <dt>Topic</dt>
                            <dd className="admin-detail-topic-value">{msg.topic}</dd>
                        </div>
                        <div>
                            <dt>Custom Reason</dt>
                            <dd>{msg.customReason || "—"}</dd>
                        </div>
                        <div>
                            <dt>From</dt>
                            <dd>
                                <div className="admin-detail-from">
                                    <strong>{msg.userName || msg.userEmail}</strong>
                                    <span>{msg.userEmail}</span>
                                </div>
                            </dd>
                        </div>
                        <div>
                            <dt>Submitted</dt>
                            <dd>{formatDate(msg.createdAt)}</dd>
                        </div>
                        <div>
                            <dt>Last Update</dt>
                            <dd>{formatDate(msg.updatedAt)}</dd>
                        </div>
                        <div>
                            <dt>Read By</dt>
                            <dd>
                                {msg.readByName ? (
                                    <>
                                        {msg.readByName}
                                        {msg.readAt && (
                                            <span className="admin-detail-meta">
                                                {" "}· {formatDate(msg.readAt)}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    "—"
                                )}
                            </dd>
                        </div>
                    </dl>

                    <div className="admin-detail-message">
                        <span className="admin-detail-message-label">Message</span>
                        <p>{msg.message}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminHelpMessageDetail;
